import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { useProductBrowser } from '@techstyle/react-components';
import { FormattedMessage, useIntl } from '@techstyle/react-intl';
import {
  updateSessionDetail,
  useSessionDetail,
  useSession,
} from '@techstyle/redux-core';

import { desktop, mobile } from '../../styles';
import { BasicModal } from '../../styles/modal';
import { isNotDualSize } from '../../utils/sizes';
import { Component as CloseButton } from '../CloseButton';
import SizeFilterModalCategoryScreen from '../SizeFilterModalCategoryScreen';
import SizeFilterModalSignUpScreen from '../SizeFilterModalSignUpScreen';

const ScreenNames = {
  SIZE_OPTIONS: 'sizeOptions',
  SIGN_UP: 'sign_up',
};

const dialogStyle = css`
  display: flex;
  overflow: visible;
  flex-direction: column;
  width: calc(100% - ${({ theme }) => theme.spacing.small}px);
  border-radius: 4px;
  padding: 4px 0px;
  ${desktop`
    width: 698px;
    position: relative;
    padding: 4px;
  `}
`;

const StyledModal = styled(BasicModal).attrs(props => ({
  springConfig: {
    tension: 230,
  },
  dialogStyle,
  overlayColor: props.theme.colors.black90,
  transitionFrom: {
    opacity: 0,
    transform: 'translate3d(0, 100%, 0)',
  },
  transitionEnter: {
    opacity: 1,
    transform: 'translate3d(0, 0, 0)',
  },
  transitionLeave: {
    opacity: 0,
    transform: 'translate3d(0, 100%, 0)',
  },
}))`
  ${mobile`
    ${BasicModal.Wrapper} {
      display: flex;
      align-items: center;
    }
  `}
`;

const ModalCloseButton = styled(CloseButton)`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
`;

const ModalContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: stretch;
  height: 100%;
`;

const ProgressBarWrapper = styled.div`
  position: absolute;
  top: 4px;
  flex-grow: 0;
  height: 17px;
  width: calc(100% - ${({ theme }) => theme.spacing.tiny}px);
  text-align: center;
  font-size: 12px;
  line-height: 14px;
  letter-spacing: 1.75px;
  text-transform: uppercase;
  color: ${({ theme, shouldChangeTextColor }) =>
    shouldChangeTextColor ? theme.colors.white : theme.colors.promo};
  z-index: 1;
  transition: color 400ms ease-in 100ms;

  ${mobile`
    top: 0px;
    width: 100%;
  `}
`;

const ProgressBar = styled.div`
  background-color: ${({ theme }) => theme.colors.lavender500};
  flex-grow: 0;
  height: ${({ theme }) => theme.spacing.tiny}px;
  width: ${props => props.percentComplete}%;
  transition: width 400ms ease-in;
  border-radius: 4px;
  ${mobile`
    border-radius: 0px;
  `}
`;

const ProgressBarTrack = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  flex-grow: 0;
  height: 8px;
  width: 100%;
  border-radius: 4px;
  ${mobile`
    border-radius: 0px;
  `}
`;

const ProgressLabel = styled.div`
  margin-top: 13px;
  color: ${({ theme }) => theme.colors.lavender500};
  font-weight: 400;
  font-size: 12px;
  line-height: 14px;
  text-align: center;
  letter-spacing: 1.75px;
  text-transform: uppercase;
`;

/** ***This component will open a Modal after 5seconds of selection on DesktopProductBrowserFilters
 ******or after click on Apply Filter button on mobile: if the page is not a Men's grids
 ******if the selected size is not dual size or unique size and if feature flag is on and if modal hasn't been seen in that session
 ***** and if at least one size category to have only one size filter selected
 *****/
export default function SizeFilterModal({
  activeCategory,
  newApplyFilters = null,
  newFilterSizeSelected = null,
  initialFilters,
}) {
  const { setFilters } = useProductBrowser() || {};
  const intl = useIntl();
  const dispatch = useDispatch();
  const isBorderfreeCustomer = useSelector(
    state => state.borderfree.isBorderfreeCustomer
  );
  const [isSizeFilterModalOpen, setIsSizeFilterModalOpen] = useState(false);
  const [hasSelectedOneSizeInOneCategory, setHasSelectedOneSizeInOneCategory] =
    useState(false);
  const [hasValidSizes, setHasValidSizes] = useState(false);
  const [currentScreenIndex, setScreenIndex] = useState(0);
  const [selectedSizesScreen1, setSelectedSizesScreen1] = useState({
    braSizes: newApplyFilters?.size_bra || [],
    braletteSizes: newApplyFilters?.size_bralette || [],
    lingerieSizes: newApplyFilters?.size_lingerie || [],
    undieSizes: newApplyFilters?.size_undie || [],
    optionSignatures: [],
  });
  const totalSteps = Object.keys(ScreenNames).length;
  const sizeFilterPromptSeenSession = useSessionDetail(
    'size_filter_prompt_seen'
  );
  const { isLoggedIn } = useSession();

  const hasModalBeenSeenThisSession =
    sizeFilterPromptSeenSession &&
    sizeFilterPromptSeenSession.networkStatus.isUpToDate &&
    sizeFilterPromptSeenSession.value;

  const hasValidCategory = activeCategory.label !== 'Men';

  // newApplyFilters.aggregationFilter contains all the filters, and we only need size filters
  const sizeFiltersLabels = [
    'size_bra',
    'size_bralette',
    'size_lingerie',
    'size_undie',
  ];

  const sizeFiltersOnly = useMemo(() => {
    return newApplyFilters
      ? sizeFiltersLabels.reduce((res, key) => {
          return { ...res, [key]: newApplyFilters[key] || [] };
        }, {})
      : {};
  }, [newApplyFilters, sizeFiltersLabels]);

  // to be valid we need at least one size category to have only one size filter selected
  useEffect(() => {
    setHasSelectedOneSizeInOneCategory(
      Object.keys(sizeFiltersOnly).some(
        sizeFilterLabel => sizeFiltersOnly[sizeFilterLabel]?.length === 1
      )
    );
    // we check if filters ONLY contain unique size or dual size. If it's a mix, it's valid
    const updatedHasValidSizes = Object.values(sizeFiltersOnly).some(
      sizeList => {
        if (sizeList.length === 1) {
          return isNotDualSize(sizeList[0]);
        }
        return false;
      }
    );
    setHasValidSizes(updatedHasValidSizes);
  }, [sizeFiltersOnly]);

  useEffect(() => {
    setSelectedSizesScreen1(prevSelection => ({
      ...prevSelection,
      braSizes: newApplyFilters?.size_bra || [],
      braletteSizes: newApplyFilters?.size_bralette || [],
      lingerieSizes: newApplyFilters?.size_lingerie || [],
      undieSizes: newApplyFilters?.size_undie || [],
      optionSignatures: [],
    }));
  }, [newApplyFilters]);

  // we don't want timer to be redefined on every re-render, here timeRef will persist
  const timerRef = useRef(null);
  const openModal = useCallback(() => {
    timerRef.current = setTimeout(() => {
      // we have to recheck because maybe people can select and unselect a size during the 5sec
      if (hasSelectedOneSizeInOneCategory && hasValidSizes) {
        setIsSizeFilterModalOpen(true);
      }
    }, 5000);
  }, [hasValidSizes, hasSelectedOneSizeInOneCategory]);

  const closeModal = () => {
    setIsSizeFilterModalOpen(false);
  };

  const onSetNewSelectedSizes = newSelectedSizes => {
    setSelectedSizesScreen1(prevSelection => ({
      ...prevSelection,
      ...newSelectedSizes,
    }));
  };

  const filterOutNonValidSizes = sizes => {
    const newArr = Object.entries(sizes).reduce((acc, [key, sizesArr]) => {
      acc[key] =
        sizesArr.length !== 1
          ? []
          : sizesArr.filter(size => !!isNotDualSize(size));
      return acc;
    }, {});
    return newArr;
  };

  const advanceScreen = () => {
    const nextScreenIndex = currentScreenIndex + 1;
    setScreenIndex(nextScreenIndex);
  };

  const handleSignUpInSuccess = useCallback(() => {
    const newFiltersToApply = {
      ...initialFilters,
      aggregationFilter: {
        ...newApplyFilters,
        size_undie: selectedSizesScreen1?.undieSizes || [],
        size_bra: selectedSizesScreen1?.braSizes || [],
        size_bralette: selectedSizesScreen1?.braletteSizes || [],
        size_lingerie: selectedSizesScreen1?.lingerieSizes || [],
      },
    };
    closeModal();
    setFilters(newFiltersToApply);
  }, [initialFilters, newApplyFilters, selectedSizesScreen1, setFilters]);

  useEffect(() => {
    if (
      hasValidCategory &&
      hasValidSizes &&
      hasSelectedOneSizeInOneCategory &&
      !hasModalBeenSeenThisSession &&
      !isBorderfreeCustomer &&
      !isLoggedIn
    ) {
      openModal();
      // Clear the interval when the component unmounts
      return () => clearTimeout(timerRef.current);
    }
  }, [
    hasValidSizes,
    hasValidCategory,
    hasSelectedOneSizeInOneCategory,
    hasModalBeenSeenThisSession,
    isBorderfreeCustomer,
    openModal,
    isLoggedIn,
  ]);

  const handleModalExit = () => {
    setIsSizeFilterModalOpen(false);
    dispatch(
      updateSessionDetail({
        name: 'size_filter_prompt_seen',
        value: 'true',
      })
    );
  };

  const renderCurrentScreen = () => {
    if (currentScreenIndex === 0) {
      return (
        <SizeFilterModalCategoryScreen
          advanceScreen={advanceScreen}
          onSetNewSelectedSizes={onSetNewSelectedSizes}
          selectedSizes={selectedSizesScreen1}
          updatedSizeFilters={
            newApplyFilters?.aggregationFilter
              ? newApplyFilters.aggregationFilter
              : newApplyFilters
          }
        />
      );
    } else {
      return (
        <SizeFilterModalSignUpScreen
          onSuccess={handleSignUpInSuccess}
          selectedSizes={filterOutNonValidSizes(selectedSizesScreen1)}
        />
      );
    }
  };

  const shouldChangeTextColor =
    currentScreenIndex && currentScreenIndex === totalSteps - 1;

  const renderProgressBar = () => {
    return (
      <ProgressBarWrapper shouldChangeTextColor={shouldChangeTextColor}>
        <ProgressBarTrack>
          <ProgressBar
            percentComplete={((currentScreenIndex + 1) / totalSteps) * 100}
          />
        </ProgressBarTrack>
        <ProgressLabel>
          <FormattedMessage
            id="site_product_grid.progress_text"
            defaultMessage="STEP {currentScreenIndex} OF {totalSteps}"
            values={{
              currentScreenIndex: currentScreenIndex + 1,
              totalSteps,
            }}
          />
        </ProgressLabel>
      </ProgressBarWrapper>
    );
  };

  return (
    <StyledModal
      closeButton={<ModalCloseButton />}
      isOpen={isSizeFilterModalOpen}
      overlayClickExits={false}
      onExit={handleModalExit}
      title={intl.formatMessage({
        id: 'site_product_grid.modal_title',
        defaultMessage: 'Size Filter Modal Title',
      })}
    >
      <ModalContent>
        {renderProgressBar()}
        {renderCurrentScreen()}
      </ModalContent>
    </StyledModal>
  );
}

SizeFilterModal.propTypes = {
  activeCategory: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  initialFilters: PropTypes.object,
  newApplyFilters: PropTypes.object,
  /*
   * Last size selected
   */
  newFilterSizeSelected: PropTypes.string,
};
